'use client';

import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/shared/services/api';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.softfactura.ro';

export interface ExportReportRequest {
  docType: string;
  columns: string[];
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  type?: string;
  clientId?: string;
  search?: string;
  direction?: string;
  sort?: string;
  order?: string;
  cozy?: boolean;
  /** Parametrii listei de pe ecran (URLSearchParams): raportul arată exact lista (filtrele, fila, coloanele). */
  filtre?: string;
}

interface JobStatus {
  status: string;
  rowCount?: number;
  token?: string;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Download a finished report by its token. Auth is cookie-based
 * (`credentials: 'include'`), mirroring the invoice-PDF download flow.
 */
export async function downloadExportReport(token: string, filename = 'raport-facturi.pdf'): Promise<void> {
  const res = await fetch(`${API_URL}/api/v1/invoices/export-report/by-token/${token}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Starts an async PDF report job, polls until done, then auto-downloads.
 * If it takes longer than the poll window, the user still gets a bell
 * notification with a download link when it completes.
 */
export function useExportReport() {
  const [pending, setPending] = useState(false);

  const start = useCallback(
    async (req: ExportReportRequest) => {
      if (pending) return;
      setPending(true);
      try {
        const { jobId } = await api.post<{ jobId: string }>('/invoices/export-report', req);
        toast.success('Raportul se generează — te anunțăm când e gata.');

        let token: string | undefined;
        for (let i = 0; i < 40; i++) {
          await sleep(3000);
          const s = await api.get<JobStatus>(`/invoices/export-report/${jobId}/status`);
          if (s.status === 'done') {
            token = s.token;
            break;
          }
          if (s.status === 'error') throw new Error('job error');
        }

        if (token) {
          await downloadExportReport(token);
          toast.success('Raportul a fost descărcat.');
        } else {
          toast.message('Raportul se generează în continuare — îl găsești în notificări când e gata.');
        }
      } catch {
        toast.error('Nu am putut genera raportul. Încearcă din nou.');
      } finally {
        setPending(false);
      }
    },
    [pending],
  );

  return { start, pending };
}
